

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { StoryboardPanel } from '@/lib/storyboard-types';

interface PanelImageViewProps {
  panel: StoryboardPanel;
  isGenerating: boolean;
  onGenerate: () => void;
  onRefine: (changeRequest: string) => void;
  onAccept: (iterationIndex: number) => void;
  isTokenDepleted?: boolean;
}

export function PanelImageView({
  panel,
  isGenerating,
  onGenerate,
  onRefine,
  onAccept,
  isTokenDepleted = false,
}: PanelImageViewProps) {
  const { t } = useTranslation();
  const [showRefineInput, setShowRefineInput] = useState(false);
  const [refineRequest, setRefineRequest] = useState('');
  const [selectedIteration, setSelectedIteration] = useState(0);

  const iterations = panel.imageIterations || [];
  const hasImages = iterations.length > 0;
  const currentImage = iterations[selectedIteration];
  const isAccepted = panel.imageStatus === 'accepted';

  const handleRefine = () => {
    if (refineRequest.trim()) {
      onRefine(refineRequest.trim());
      setRefineRequest('');
      setShowRefineInput(false);
    }
  };

  const handleAccept = () => {
    onAccept(selectedIteration);
  };

  // Not ready state - script not accepted yet
  if (panel.scriptStatus !== 'accepted') {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          bgcolor: 'action.hover',
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('storyboard.panel_image.accept_script_notice')}
        </Typography>
      </Box>
    );
  }

  // Generating state
  if (isGenerating) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          bgcolor: 'action.hover',
          borderRadius: 2,
        }}
      >
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          {t('storyboard.panel_image.generating')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('storyboard.panel_image.generation_time_notice')}
        </Typography>
      </Box>
    );
  }

  // No images yet
  if (!hasImages) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          bgcolor: 'action.hover',
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'divider',
        }}
      >
        <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('storyboard.panel_image.no_illustration')}
        </Typography>
        <Tooltip title={isTokenDepleted ? t('entity_image_manager.common.tokens_depleted_tooltip') : ''} placement="top">
          <div style={{ width: '100%' }}>
            <Button variant="contained" onClick={onGenerate} startIcon={<ImageIcon />} disabled={isTokenDepleted} fullWidth>
              {t('storyboard.panel_image.generate_illustration')}
            </Button>
          </div>
        </Tooltip>
      </Box>
    );
  }

  // Has images - show viewer
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Main Image */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'background.paper',
          borderRadius: 2,
          overflow: 'hidden',
          border: isAccepted ? 2 : 1,
          borderColor: isAccepted ? 'success.main' : 'divider',
        }}
      >
        <Box
          component="img"
          src={currentImage.imageUrl}
          alt={t('storyboard.panel_image.alt_panel_illustration', { order: panel.order })}
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
        {isAccepted && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'success.main',
              color: 'white',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <CheckCircleIcon fontSize="small" />
            <Typography variant="caption" fontWeight={600}>
              {t('storyboard.panel_image.accepted')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Iteration Thumbnails (if more than 1) */}
      {iterations.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
          {iterations.map((iteration, index) => (
            <Box
              key={iteration.id}
              onClick={() => setSelectedIteration(index)}
              sx={{
                width: 60,
                height: 60,
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'pointer',
                border: 2,
                borderColor: selectedIteration === index ? 'primary.main' : 'transparent',
                opacity: selectedIteration === index ? 1 : 0.7,
                transition: 'all 0.2s',
                flexShrink: 0,
                '&:hover': { opacity: 1 },
              }}
            >
              <Box
                component="img"
                src={iteration.imageUrl}
                alt={t('storyboard.panel_image.alt_iteration', { index: index + 1 })}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
          ))}
        </Box>
      )}

      {/* Actions */}
      {!isAccepted && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<CheckCircleIcon />}
            onClick={handleAccept}
          >
            {t('storyboard.panel_image.accept')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={() => setShowRefineInput(!showRefineInput)}
          >
            {t('storyboard.panel_image.refine')}
          </Button>
          <Tooltip title={isTokenDepleted ? t('entity_image_manager.common.tokens_depleted_tooltip') : t('storyboard.panel_image.generate_new')}>
            <span>
              <IconButton size="small" onClick={onGenerate} disabled={isTokenDepleted}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Refinement Input */}
      {showRefineInput && !isAccepted && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            placeholder={t('storyboard.panel_image.refine_placeholder')}
            value={refineRequest}
            onChange={(e) => setRefineRequest(e.target.value)}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={!refineRequest.trim() || isTokenDepleted}
              onClick={handleRefine}
            >
              {isTokenDepleted ? t('storyboard.panel_image.tokens_exhausted') : t('storyboard.panel_image.apply_changes')}
            </Button>
            <Button size="small" onClick={() => setShowRefineInput(false)}>
              {t('storyboard.panel_image.cancel')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
